/**
 * Unified AI Panel — Phase 6
 *
 * Single assistant surface for section-scoped planning, drafting, and revision.
 *
 * Parent spec §2, §12 Stage 3
 * Spec 01 §10.3-§10.5 (NDJSON streaming)
 * Spec 06 §2 (confirmation flow)
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import type { ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  X,
  Square,
  ArrowUp,
  Library,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  BookOpen,
  TextSelect,
  ListChecks,
  PenLine,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Check,
  Lock,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  useAgentStore,
  type AgentChatMessage,
  type AgentCitationSource,
  type SuggestedAction,
  type SuggestedActionOffer,
} from '@/stores/agent-store'
import { useActiveTurnPoller } from '@/hooks/useActiveTurnPoller'
import { classifySelectionIntent } from '@/lib/api'
import type { PlanSuggestion } from '@/lib/api'
import { usePreviewStore } from '@/stores/preview-store'
import { resolveCitationTargetPage, toPrecisePreviewHighlight } from '@/lib/citation-preview'
import {
  groupCitationsByDocument,
  type GroupedCitationDoc,
} from '@/lib/group-citation-chips'
import {
  remarkAttAttributions,
  remarkCitations,
  remarkLibraryCitations,
  normalizeCitationMarkdown,
} from '@/lib/remark-citations'
import { AttAttributionNodeComponent } from '@/components/assistant/AttAttributionChip'
import { parseUsedCitations, parseUsedLibraryCitations } from '@/lib/parse-used-citations'
import { useDraftingStore } from '@/stores/drafting-store'
import { useDocumentsStore } from '@/stores/documents-store'
import { BrandIcon } from '@/components/BrandIcon'
import { EvidenceSelectDialog, type EvidenceScope } from '@/components/drafting/EvidenceSelectDialog'
import { requirementRefLabels, useRequirementDirectory } from '@/components/requirement-coverage/useRequirementDirectory'
import { ThreadPicker } from '@/components/drafting/ThreadPicker'
import { ThreadCreateButton } from '@/components/drafting/ThreadCreateButton'
import { ChatAttachments } from '@/components/assistant/ChatAttachments'
import { AttachmentButton } from '@/components/assistant/AttachmentButton'
import { AttachmentChips } from '@/components/assistant/AttachmentChips'
import { useAssistantAttachments } from '@/hooks/use-assistant-attachments'
import {
  MAX_CHAT_ATTACHMENTS,
  MAX_CHAT_TOTAL_SIZE,
} from '@/lib/assistant-attachment-validation'
import { DRAFT_STASH_KEYS, consumeDraftStash } from '@/lib/draft-stash'
import { handleUnwrappingPaste } from '@/lib/unwrap-pasted-text'
import { ActivityPhases } from '@/components/chat/ActivityPhases'
import { hydratePhasesFromSteps } from '@/lib/activity-phases'
import { PlanCard } from '@/components/drafting/PlanCard'
import { isEditablePlanVersion } from '@/lib/plan-edit'
import { ReviewTab } from '@/components/drafting/ReviewTab'
import { CommentsTab } from '@/components/drafting/CommentsTab'
import type { CommentJumpContext } from '@/components/drafting/CommentCard'
import type { BidMemberInfo } from '@/components/ui/owner-assignment'
import { useCommentsStore } from '@/stores/comments-store'
import { useAuthStore } from '@/stores/auth-store'
import { hasUnreadThreads, unreadReference } from '@/lib/comment-unread'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { MemorySavedLines } from '@/components/chat/MemorySavedLines'
import { PanelAppliedEditLines } from '@/components/drafting/AppliedEditNotice'
import { PanelPendingSuggestions } from '@/components/drafting/PanelPendingSuggestions'
import { PanelDocumentJob } from '@/components/drafting/PanelDocumentJob'
import { PanelQuestions } from '@/components/drafting/PanelQuestions'
import { useUnifiedAiPanelStore, type AiPanelTab } from '@/stores/unified-ai-panel-store'
import { useBidReadOnly } from '@/hooks/useViewOnly'
import { ViewOnlyNotice } from '@/components/ViewOnlyNotice'

// ===================
// Types
// ===================

interface UnifiedAiPanelProps {
  bidId: string
  artifactId: string
  sectionKey: string
  scopeKind?: 'unit' | 'artifact'
  bidMembers?: BidMemberInfo[] | undefined
  /** Route context the Comments tab needs to jump across section editors. */
  commentJumpContext?: CommentJumpContext | undefined
  /**
   * With commentsOnly: also expose the Review tab. Set when the read-only user
   * is the section's designated reviewer — AI review + promote-to-comment are
   * reviewer tools (canPromoteFromReview is reviewer-only), but Ask AI (the
   * `write` tab) stays hidden. No effect when commentsOnly is false.
   */
  reviewerTabs?: boolean
  /**
   * Comments-only surface for readers (A0). Read-only users still need to
   * comment during review, but must not reach the Ask AI (the `write` tab) /
   * Review AI tools. Their triggers and bodies are not rendered at all — the
   * Ask AI body owns the Draft Agent session bootstrap, so leaving it
   * unmounted is what keeps a reader from starting a session.
   */
  commentsOnly?: boolean
  /**
   * Replaces the editor's Checks body. The full-response reader hosts its own
   * whole-document checks surface but reuses this tab shell.
   */
  renderChecks?: (() => ReactNode) | undefined
  /**
   * Which read-only tab to show. Only meaningful with commentsOnly +
   * reviewerTabs — the full-response reader flips it to 'review' when the
   * reader clicks an issue marker in the margin.
   */
  initialTab?: 'review' | 'comment'
  /**
   * Advice-only (R1 E1f; Kapil decision 2026-10-08): the document is
   * finalised (`finalized`) or in review (`pending_review`), so the AI may
   * advise and change nothing. Ask AI stays, with a one-line note; it shows
   * no edit, suggestion, Undo or job affordance, and a question about
   * selected text is always a question. Checks only for the section's
   * reviewer (`reviewerTabs`), as on any read-only surface. Pass it with
   * `commentsOnly` false. The server withholds every tool that would change
   * the document on such a turn; this mode keeps the panel from offering it.
   */
  adviceOnly?: AdviceOnlyReason | undefined
  /**
   * Names the panel's scope above its tabs (R4 §2.8), e.g. "This section".
   * Display only; the page passes the wording that fits it.
   */
  scopeLabel?: string | undefined
  /**
   * Replaces the drafting empty state (plan CTA, capability chips) with one
   * line, for a surface that is not a drafted document — a buyer form.
   */
  emptyHint?: string | undefined
}

/** Why the panel is advice-only: the document's status (R1 E1f). */
type AdviceOnlyReason = 'finalized' | 'pending_review'

/** The advice-only panel's one-line note. Placeholder copy until Kapil's hand test (R1 E1f). */
const ADVICE_ONLY_NOTES: Record<AdviceOnlyReason, string> = {
  finalized: 'This document is finalised, so the AI can advise but not change it.',
  pending_review: 'This document is in review, so the AI can advise but not change it.',
}

// ===================
// Proposal Chat Stub
// ===================
//
// Inline summary of an AI-proposed draft patch, rendered in the chat thread.
// The diff itself stays in the editor (in context with the draft), and so do
// Accept/Reject — the editor's decision strip is the single decision point.
// This stub is the conversational record plus a jump link: pending state
// offers "Review in editor", resolved state collapses to a status line.

interface ProposalChatStubProps {
  proposal: NonNullable<AgentChatMessage['proposal']>
  /** Advice-only (R1 E1f): the record stays, without the jump to review it in the editor. */
  adviceOnly?: boolean
}

function ProposalChatStub({ proposal, adviceOnly = false }: ProposalChatStubProps) {
  const draftPatchPreview = useAgentStore((s) => s.draftPatchPreview)

  // The link only means something for the live preview — once accepted or
  // rejected the draftPatchPreview clears, but a stale stub for the same
  // proposal could still be rendered. Guard with a proposalId match.
  const isLive = draftPatchPreview?.proposalId === proposal.proposalId
  const sectionLabel = proposal.sectionName ?? 'this section'

  if (proposal.status === 'pending') {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-ai/30 bg-ai-soft/30 px-3 py-2">
        <Sparkles className="h-4 w-4 text-ai-text mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            Proposed change to <span className="font-medium">{sectionLabel}</span> — review in the editor.
          </p>
          {isLive && !adviceOnly && (
            <button
              type="button"
              onClick={() => {
                const root = document.querySelector('.ProseMirror')
                const target =
                  root?.querySelector('[data-hunk-id]') ?? root?.querySelector('ins, del')
                target?.scrollIntoView({ block: 'center', behavior: 'smooth' })
              }}
              className="mt-1 text-xs text-[hsl(var(--link))] hover:underline"
            >
              Review in editor
            </button>
          )}
        </div>
      </div>
    )
  }

  const Icon = proposal.status === 'accepted' ? Check : X
  const tone = proposal.status === 'accepted' ? 'text-[hsl(var(--status-success))]' : 'text-muted-foreground'
  return (
    <div className="flex items-center gap-1.5 px-1 text-meta">
      <Icon className={cn('h-3 w-3', tone)} />
      <span>
        Proposed change to <span className="font-medium text-foreground">{sectionLabel}</span> — {proposal.status}.
      </span>
    </div>
  )
}

// ===================
// Plan Chat Card
// ===================
//
// Renders a structured `role: 'plan'` chat message as a PlanCard. Binds the
// accept action from the store ("Accept & draft" → approve the plan, then run
// the drafter on it). When the brief moved on after this plan was generated the
// card renders stale (§4.3a-C3): Accept no-ops and the writer is routed to
// "Regenerate plan", which re-runs generation via a write-intent turn.

function PlanChatCard({ meta }: { meta: NonNullable<AgentChatMessage['plan']> }) {
  const acceptPlanMessage = useAgentStore((s) => s.acceptPlanMessage)
  const rejectPlanMessage = useAgentStore((s) => s.rejectPlanMessage)
  const regeneratePlan = useAgentStore((s) => s.regeneratePlan)
  const acceptPlanSuggestion = useAgentStore((s) => s.acceptPlanSuggestion)
  const dismissPlanSuggestion = useAgentStore((s) => s.dismissPlanSuggestion)
  const restorePlanSuggestion = useAgentStore((s) => s.restorePlanSuggestion)
  const isStale = useAgentStore((s) => s.stalePlanIds.has(meta.planId))
  const bidId = useAgentStore((s) => s.sessionInitContext?.bidId ?? null)
  // Edit mode IS the presence of a draft (Task 2 contract) — accept, reject and
  // a terminal save all drop it, so this needs no status check of its own.
  const editDraft = useAgentStore((s) => s.planEdits[meta.planId] ?? null)
  const updatePlanSection = useAgentStore((s) => s.updatePlanSection)
  const movePlanSection = useAgentStore((s) => s.movePlanSection)
  const removePlanSection = useAgentStore((s) => s.removePlanSection)
  const addPlanKeyPoint = useAgentStore((s) => s.addPlanKeyPoint)
  const updatePlanKeyPoint = useAgentStore((s) => s.updatePlanKeyPoint)
  const removePlanKeyPoint = useAgentStore((s) => s.removePlanKeyPoint)
  const revertPlanEdits = useAgentStore((s) => s.revertPlanEdits)
  const savePlanEdits = useAgentStore((s) => s.savePlanEdits)
  // The card's regenerate entry points are the only ones that reach `sendTurn`
  // from outside the composer, so they carry the composer's gate themselves.
  // Without it a second turn overwrites the module-singleton abort controller
  // (Stop then only cancels the newest while the first stream keeps writing),
  // and after an accept `isPolling` makes `sendTurn` reject with a banner.
  const isStreaming = useAgentStore((s) => s.isStreaming)
  const isPolling = useAgentStore((s) => s.isPolling)
  const isCancellable = useAgentStore((s) => s.isCancellable)
  const isTurnBusy = isStreaming || isPolling || isCancellable
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [acceptingSuggestionId, setAcceptingSuggestionId] = useState<string | null>(null)
  // `isTurnBusy` alone cannot debounce a double-click: `sendTurn` awaits a draft
  // flush and a token fetch before it raises `isStreaming`, so a second click
  // inside that window slips through. Same synchronous latch `handleSend` uses.
  const regenerateInflightRef = useRef(false)

  // coverageMap carries requirement ids only; resolve them to titles for display.
  // The status rides along so the card can show a loading/failed line instead of
  // falling back to bare UUIDs while the directory is unresolved.
  const { entries: directory, status: directoryStatus } = useRequirementDirectory(bidId)
  const requirementNames = useMemo(
    () => Object.fromEntries((directory ?? []).map((entry) => [entry.id, entry.title])),
    [directory]
  )
  const requirementRefs = useMemo(() => requirementRefLabels(directory), [directory])

  // Both handlers are invoked as `void handle…()`, so an escaping rejection
  // would be swallowed by main.tsx's ApiRequestError bypass. The store catches
  // failures and puts them on the error banner; these catches are the backstop
  // that guarantees nothing ever escapes unhandled.
  // Save commits the draft on its own, so edits survive without approving the
  // plan. `savePlanEdits` owns every failure path (banner + inline flags) and
  // drops the draft on success, so there is nothing to do with the result here.
  const handleSave = async () => {
    setIsSaving(true)
    try {
      await savePlanEdits(meta.planId)
    } catch {
      // Already surfaced by the store's error banner.
    } finally {
      setIsSaving(false)
    }
  }

  // Accept STILL saves first, even with a Save button beside it: a writer who
  // edits and then goes straight for "Accept & draft" must not have the drafter
  // run on the text they replaced. A failed save aborts the accept — the store
  // has already put the reason on the panel's error banner.
  // (`savePlanEdits` on a clean draft just leaves edit mode, no round trip.)
  //
  // DECIDED, not overlooked — and the decision is to accept a SILENT LOSS:
  //
  // The row edit controls (title, word count, key points, add/remove/move) stay
  // LIVE across both legs of the accept. Only the footer buttons are disabled.
  // That is on purpose — the editor is deliberately not locked mid-request —
  // but it means the window is wide open, and those live controls are precisely
  // the mechanism by which text gets typed into it. The internal save may then
  // legitimately rebase and keep that text (see `savePlanEdits`), after which
  // `acceptPlanMessage` discards the draft outright.
  //
  // What the writer sees: the words they just typed disappear as the card
  // re-renders the accepted plan. No notice, no undo. The drafter runs on the
  // plan as it was SENT, not as it was left.
  //
  // Accepted anyway, because the alternative is worse: aborting the accept and
  // asking them to press it again turns a sub-second race into a dead end on
  // the card's primary action, for a plan that can no longer be edited at all
  // (an approved plan 400s on PATCH), so there is nowhere for the kept text to
  // go. If this ever needs to stop being silent, the place to say so is here —
  // compare the draft after the save and warn before approving.
  const handleAccept = async () => {
    setIsAccepting(true)
    try {
      if (editDraft) {
        const result = await savePlanEdits(meta.planId)
        if (!result.ok) return
      }
      await acceptPlanMessage(meta.planId)
    } catch {
      // Already surfaced by the store's error banner.
    } finally {
      setIsAccepting(false)
    }
  }

  // `rejectPlanMessage` never throws (it owns its catch — main.tsx's
  // unhandled-rejection handler skips ApiRequestError), so this only has to
  // clear the pending flag.
  const handleReject = async () => {
    setIsRejecting(true)
    try {
      await rejectPlanMessage(meta.planId)
    } finally {
      setIsRejecting(false)
    }
  }

  // Drives both "Regenerate plan" (pending card) and "Re-plan section"
  // (accepted card) — one store action, one gate.
  const handleRegenerate = async () => {
    if (isTurnBusy || regenerateInflightRef.current) return
    regenerateInflightRef.current = true
    try {
      await regeneratePlan()
    } finally {
      regenerateInflightRef.current = false
    }
  }

  const handleAcceptSuggestion = async (suggestion: PlanSuggestion) => {
    setAcceptingSuggestionId(suggestion.id)
    try {
      await acceptPlanSuggestion(meta.planId, suggestion)
    } catch {
      // Already surfaced by the store's error banner.
    } finally {
      setAcceptingSuggestionId(null)
    }
  }

  return (
    <PlanCard
      plan={meta.plan}
      bidId={bidId}
      status={meta.status}
      stale={isStale}
      isAccepting={isAccepting}
      requirementNames={requirementNames}
      requirementNamesStatus={directoryStatus}
      requirementRefs={requirementRefs}
      suggestions={meta.suggestions}
      onAcceptSuggestion={(s) => void handleAcceptSuggestion(s)}
      onDismissSuggestion={(id) => dismissPlanSuggestion(meta.planId, id)}
      isAcceptingSuggestionId={acceptingSuggestionId}
      dismissedSuggestion={meta.dismissedSuggestion?.suggestion ?? null}
      onRestoreSuggestion={() => restorePlanSuggestion(meta.planId)}
      onAccept={() => void handleAccept()}
      onRegenerate={() => void handleRegenerate()}
      isTurnBusy={isTurnBusy}
      onReject={() => void handleReject()}
      isRejecting={isRejecting}
      isDirty={editDraft?.isDirty ?? false}
      onSave={() => void handleSave()}
      // Both entry points, one flag. The store raises `draft.isSaving` for
      // ANY save — including the one Accept runs internally — so folding it in
      // is what stops Revert or Reject firing while the PUT that persists those
      // very edits is still in flight.
      isSaving={isSaving || editDraft?.isSaving === true}
      onRevert={() => revertPlanEdits(meta.planId)}
      // A card hydrated without a usable version can never commit an edit
      // (`expectedVersion` must be a positive int, and both hydration paths fall
      // back to 0 when the server omits one). The store refuses to fork a draft
      // in that state, so passing the handlers would render an editor that
      // silently swallows every keystroke. Withholding `onUpdateSection`
      // narrows PlanCard's handler union to null — a read-only card, which is
      // the honest rendering of "this plan can't be edited right now".
      {...(isEditablePlanVersion(meta.version)
        ? {
            editDraft,
            onUpdateSection: (
              sectionId: string,
              patch: { heading?: string; targetWordCount?: number }
            ) => updatePlanSection(meta.planId, sectionId, patch),
            onMoveSection: (sectionId: string, direction: -1 | 1) =>
              movePlanSection(meta.planId, sectionId, direction),
            onRemoveSection: (sectionId: string) => removePlanSection(meta.planId, sectionId),
            onAddKeyPoint: (sectionId: string) => addPlanKeyPoint(meta.planId, sectionId),
            onUpdateKeyPoint: (sectionId: string, keyPointId: string, text: string) =>
              updatePlanKeyPoint(meta.planId, sectionId, keyPointId, text),
            onRemoveKeyPoint: (sectionId: string, keyPointId: string) =>
              removePlanKeyPoint(meta.planId, sectionId, keyPointId),
          }
        : {})}
    />
  )
}

// ===================
// Approved Plan (history) Card
// ===================
//
// An approved plan put back from history after a reload or section switch
// (Kapil, 2026-09-24): collapsed behind "Plan approved", expandable to read
// the plan. Read-only — no edit handlers, no Accept / Reject, and no Re-plan
// (a transcript card is a record, not a control). Live, a plan a turn drafted
// from — its own or a later "draft it" — lands here too (approved at turn end,
// Kapil 2026-09-28); a card the writer accepts with the button is still
// `PlanChatCard`.

function ApprovedPlanHistoryCard({ meta }: { meta: NonNullable<AgentChatMessage['plan']> }) {
  const bidId = useAgentStore((s) => s.sessionInitContext?.bidId ?? null)
  const { entries: directory, status: directoryStatus } = useRequirementDirectory(bidId)
  const requirementNames = useMemo(
    () => Object.fromEntries((directory ?? []).map((entry) => [entry.id, entry.title])),
    [directory]
  )
  const requirementRefs = useMemo(() => requirementRefLabels(directory), [directory])

  return (
    <PlanCard
      plan={meta.plan}
      status="accepted"
      requirementNames={requirementNames}
      requirementNamesStatus={directoryStatus}
      requirementRefs={requirementRefs}
      collapsedLabel="Plan approved"
    />
  )
}

// ===================
// Advice-only Plan Card (R1 E1f)
// ===================
//
// A plan from the conversation, in a panel on a finalised or in-review
// document: the plan reads as it is, at its own status, with nothing to act
// on (no Accept, Reject, Regenerate, edits or "also worth including"). An
// approved plan from history keeps its collapsed "Plan approved" card.

function AdviceOnlyPlanCard({ meta }: { meta: NonNullable<AgentChatMessage['plan']> }) {
  const bidId = useAgentStore((s) => s.sessionInitContext?.bidId ?? null)
  const { entries: directory, status: directoryStatus } = useRequirementDirectory(bidId)
  const requirementNames = useMemo(
    () => Object.fromEntries((directory ?? []).map((entry) => [entry.id, entry.title])),
    [directory]
  )
  const requirementRefs = useMemo(() => requirementRefLabels(directory), [directory])

  return (
    <PlanCard
      plan={meta.plan}
      status={meta.status}
      requirementNames={requirementNames}
      requirementNamesStatus={directoryStatus}
      requirementRefs={requirementRefs}
      readOnly
    />
  )
}

// ===================
// Message Bubble
// ===================

/** User text gets GFM only — no citation or attachment-token plugins. */
const USER_REMARK_PLUGINS: Parameters<typeof ReactMarkdown>[0]['remarkPlugins'] = [remarkGfm]

/**
 * A user types a newline meaning a new line; Markdown folds a single newline
 * into a space. Turn each lone newline into a hard break (two trailing spaces)
 * and leave blank-line paragraph breaks alone.
 */
function preserveUserLineBreaks(text: string): string {
  return text.replace(/([^\n])\n(?!\n)/g, '$1  \n')
}

/**
 * How a reply's cited labels are displayed, and in what order the chips list
 * them. Only labels that open a source count: an entry with no `documentId`
 * is a hole in a saved numbering and opens nothing.
 *
 * - A turn that wrote a draft (`ownLabels`) shows each marker's OWN number,
 *   lowest first. Its markers are the saved draft's numbering, which the editor
 *   shows by the saved ref's `citationIndex`; renumbering them made the panel's
 *   "L1" and the editor's "[L4]" the same document (dev 2026-09-29).
 * - A question-only turn has no editor to match, so it keeps the compact
 *   display: 1..n by first appearance.
 */
function citationDisplay(
  used: number[],
  sources: AgentCitationSource[] | undefined,
  ownLabels: boolean
): { usedCitations: number[]; origToDisplay: Map<number, number> } {
  const cited = used.filter((label) => isOpenableSource(sources?.[label - 1]))
  if (ownLabels) {
    const usedCitations = [...cited].sort((a, b) => a - b)
    return { usedCitations, origToDisplay: new Map(usedCitations.map((label) => [label, label])) }
  }
  return { usedCitations: cited, origToDisplay: new Map(cited.map((label, i) => [label, i + 1])) }
}

function isOpenableSource(source: AgentCitationSource | undefined): source is AgentCitationSource {
  return Boolean(source?.documentId)
}

interface MessageBubbleProps {
  message: AgentChatMessage
  /** Advice-only (R1 E1f): no Undo line, no plan actions, no jump to review a proposal. */
  adviceOnly?: boolean
}

function MessageBubble({ message, adviceOnly = false }: MessageBubbleProps) {
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'
  const undoMemorySaved = useAgentStore((s) => s.undoMemorySaved)
  const sources = message.sources
  const sourceCount = sources?.length ?? 0
  const librarySources = message.librarySources
  const librarySourceCount = librarySources?.length ?? 0

  // Remark plugins — add citation plugins when sources are available and not streaming
  const remarkPlugins = useMemo(() => {
    const plugins: Parameters<typeof ReactMarkdown>[0]['remarkPlugins'] = [remarkGfm]
    // Attachment attribution tokens (spec 19). Always on — the orchestrator
    // feeding this panel injects the guidance that teaches `[ATT:]`, so this
    // surface receives the token as routinely as the shared chat bubble does.
    // Registered ahead of every citation plugin: those normalize bracket shapes
    // and rewrite text nodes in place, so the token must be lifted out first.
    plugins.push(remarkAttAttributions())
    if (sourceCount > 0 && !message.isStreaming) {
      plugins.push(remarkCitations(sourceCount))
    }
    if (librarySourceCount > 0 && !message.isStreaming) {
      plugins.push(remarkLibraryCitations(librarySourceCount))
    }
    return plugins
  }, [sourceCount, librarySourceCount, message.isStreaming])

  // One normalized string feeds BOTH parse and render. Normalizing only one
  // side makes inline badge numbers disagree with the rail (first-appearance
  // order changes when a leading marker run moves).
  const normalizedContent = useMemo(
    () => normalizeCitationMarkdown(message.content),
    [message.content]
  )

  // A writing turn shows the editor's labels; a question, 1..n (see citationDisplay).
  const ownLabels = message.citationLabels === 'saved'
  const citationMapping = useMemo(
    () => citationDisplay(parseUsedCitations(normalizedContent), sources, ownLabels),
    [normalizedContent, sources, ownLabels]
  )
  const libraryCitationMapping = useMemo(
    () => citationDisplay(parseUsedLibraryCitations(normalizedContent), librarySources, ownLabels),
    [normalizedContent, librarySources, ownLabels]
  )

  // Stable keys for ReactMarkdown remounting
  const sourcesKey = sources
    ?.map((s) => `${s.documentId}:${s.pageNumber ?? ''}`)
    .join('|') ?? 'no-sources'
  const librarySourcesKey = librarySources
    ?.map((s) => `${s.documentId}:${s.pageNumber ?? ''}`)
    .join('|') ?? 'no-lib-sources'

  // Custom component mapping for citation nodes
  const components = useMemo(() => {
    const hasSources = sources && sources.length > 0
    const hasLibSources = librarySources && librarySources.length > 0

    const mapping: Record<string, React.ComponentType<Record<string, unknown>>> = {}

    // Always mapped, and why there is no early `return undefined` here: the ATT
    // plugin runs on every message, and an unmapped custom element renders as an
    // empty tag — which would swallow the filename entirely rather than show it.
    mapping['att-attribution'] = AttAttributionNodeComponent

    if (hasSources) {
      mapping['citation'] = ({ index: origIndex }: { index?: number }) => {
        if (origIndex === undefined || origIndex < 1 || origIndex > sources.length) {
          return <span>[{origIndex}]</span>
        }
        const displayIndex = citationMapping.origToDisplay.get(origIndex)
        const source = sources[origIndex - 1]
        if (displayIndex === undefined || !isOpenableSource(source)) return <span>[{origIndex}]</span>
        return (
          <AgentCitationButton
            displayIndex={displayIndex}
            source={source}
          />
        )
      }
    }

    if (hasLibSources) {
      mapping['library-citation'] = ({ index: origIndex }: { index?: number }) => {
        if (origIndex === undefined || origIndex < 1 || origIndex > librarySources.length) {
          return <span>[L{origIndex}]</span>
        }
        const displayIndex = libraryCitationMapping.origToDisplay.get(origIndex)
        const source = librarySources[origIndex - 1]
        if (displayIndex === undefined || !isOpenableSource(source)) return <span>[L{origIndex}]</span>
        return (
          <AgentLibraryCitationButton
            displayIndex={displayIndex}
            source={source}
          />
        )
      }
    }

    return mapping
  }, [sourcesKey, librarySourcesKey, citationMapping, libraryCitationMapping, sources, librarySources])

  // Early returns for non-chat roles (after all hooks)
  if (message.role === 'status') {
    const isTransient = message.eventType === 'status' || message.eventType === undefined
    const StatusIcon = isTransient
      ? null
      : message.eventType === 'async_cancelled'
        ? XCircle
        : CheckCircle2
    return (
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={cn(
          'flex items-center gap-1.5 py-1 text-sm font-medium text-muted-foreground',
          'transition-opacity duration-500 motion-reduce:transition-none',
          message.isExiting && 'opacity-0'
        )}
      >
        {StatusIcon ? (
          <StatusIcon className="h-3 w-3 shrink-0" />
        ) : (
          <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground animate-pulse" />
        )}
        {/* The trailing ellipsis reads as "still going", so it belongs only on
            transient narration — never on a finished/cancelled line. */}
        <span>{isTransient ? `${message.content.replace(/\.+$/, '')}...` : message.content}</span>
      </div>
    )
  }

  if (message.role === 'system') {
    const userFriendlySummary = message.content
      .replace(/\bthreads\b/gi, 'chats')
      .replace(/\bthread\b/gi, 'chat')
    return (
      <div className="rounded-md border border-ai/30 bg-ai-soft/40 px-3 py-2">
        <p className="text-[11px] font-medium text-ai-text mb-1">Carry-forward summary</p>
        <div className="chat-markdown prose prose-sm max-w-none dark:prose-invert">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{userFriendlySummary}</ReactMarkdown>
        </div>
      </div>
    )
  }

  if (message.role === 'proposal' && message.proposal) {
    return <ProposalChatStub proposal={message.proposal} adviceOnly={adviceOnly} />
  }

  if (message.role === 'plan' && message.plan) {
    if (message.plan.collapsed) return <ApprovedPlanHistoryCard meta={message.plan} />
    return adviceOnly ? <AdviceOnlyPlanCard meta={message.plan} /> : <PlanChatCard meta={message.plan} />
  }

  // No avatar on either side — see MessageBubble.
  return (
    <div className="flex" data-testid="chat-bubble">
      {/* Content */}
      <div className={cn('flex-1 flex flex-col gap-1 min-w-0', isUser && 'items-end')}>
        <div
          className={cn(
            // The 85% cap is a USER-bubble affordance — it keeps the tinted
            // bubble from spanning the panel so the two sides read apart.
            // Assistant output is bare prose and takes the full column; capping
            // it just strands ~15% of the reading width as dead space.
            'text-sm',
            isUser && 'max-w-[85%] rounded-lg px-3 py-2 bg-muted'
          )}
        >
          {isUser ? (
            <div className="space-y-1.5">
              {message.attachments && (
                <AttachmentChips attachments={message.attachments} className="mb-0" />
              )}
              {message.quotedText && (
                <blockquote className="border-l-2 border-ai/40 pl-2 text-meta italic">
                  &ldquo;{message.quotedText}&rdquo;
                </blockquote>
              )}
              {/* Markdown, like the assistant side: a "Fix with AI" seed arrives
                  as `Fix this issue in *X*:\n**Y:** …` and read as literal
                  asterisks when printed raw. Single newlines stay line breaks. */}
              <div className="chat-markdown prose prose-sm max-w-none break-words dark:prose-invert">
                <ReactMarkdown remarkPlugins={USER_REMARK_PLUGINS}>
                  {preserveUserLineBreaks(message.content)}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            // chat-markdown-panel: the reply's bold labels (**Open items:**,
            // **Next step:**) render semibold in the foreground token.
            // Panel replies only; the user bubble and the Assistant page keep
            // the chat default.
            <div className="chat-markdown chat-markdown-panel prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown
                key={`md-${message.id}-${sourcesKey}`}
                remarkPlugins={remarkPlugins}
                components={components}
              >
                {normalizedContent}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Source chips below message */}
        {isAssistant && sources && sources.length > 0 && !message.isStreaming && (
          <div className="flex flex-wrap gap-2 mt-1">
            {groupCitationsByDocument(
              citationMapping.usedCitations,
              citationMapping.origToDisplay,
              sources,
            ).map((group) => (
              <SourceChip
                key={group.documentId}
                group={group}
                sources={sources}
                icon={<FileText className="h-3 w-3" />}
              />
            ))}
          </div>
        )}

        {/* Library source chips */}
        {isAssistant && librarySources && librarySources.length > 0 && !message.isStreaming && libraryCitationMapping.usedCitations.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="text-[10px] font-medium text-muted-foreground self-center">Library:</span>
            {groupCitationsByDocument(
              libraryCitationMapping.usedCitations,
              libraryCitationMapping.origToDisplay,
              librarySources,
            ).map((group) => (
              <SourceChip
                key={`lib-${group.documentId}`}
                group={group}
                sources={librarySources}
                prefix="L"
                icon={<BookOpen className="h-3 w-3" />}
              />
            ))}
          </div>
        )}

        {/* "Saved to memory · Undo" — one line per item the AI saved this reply. */}
        {isAssistant && (
          <MemorySavedLines
            lines={message.memorySaved}
            onUndo={(memoryId) => void undoMemorySaved(message.id, memoryId)}
          />
        )}

        {/* "Updated Methodology · Undo" — AI edits this reply applied (R1). Not on an advice-only panel (R1 E1f). */}
        {isAssistant && !adviceOnly && <PanelAppliedEditLines messageId={message.id} />}
      </div>
    </div>
  )
}

// ===================
// Citation Buttons (inline superscript in markdown)
// ===================

function AgentCitationButton({ displayIndex, source }: { displayIndex: number; source: AgentCitationSource }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => {
              const precise = toPrecisePreviewHighlight(
                source.highlightData ?? undefined,
                source.quote ?? null,
                `agent-${source.documentId}-${displayIndex}`,
              )
              const targetPage = resolveCitationTargetPage(
                precise?.highlight ? { highlight: precise.highlight } : undefined,
                source.pageNumber,
              )
              void usePreviewStore.getState().openPreview({
                documentId: source.documentId,
                documentName: source.documentName,
                ...(targetPage != null && { page: targetPage }),
                context: 'chat',
                citationLabel: `Source [${displayIndex}]`,
                ...(precise?.highlight ? { highlight: precise.highlight } : {}),
                ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
              })
            }}
            className="inline-flex items-center justify-center text-[10px] font-semibold text-muted-foreground bg-muted hover:bg-muted/70 rounded-full ml-0.5 px-1 py-0.5 align-super leading-none transition-colors cursor-pointer"
            aria-label={`Source ${displayIndex}: ${source.documentName}`}
          >
            {displayIndex}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] bg-popover text-popover-foreground border shadow-md">
          <p className="text-xs font-medium">{source.documentName}</p>
          {source.pageNumber ? (
            <p className="text-meta">Page {source.pageNumber}</p>
          ) : null}
          {(source.quote ?? source.snippet) && (
            <p className="text-meta mt-1 line-clamp-3">
              &ldquo;{source.quote ?? source.snippet}&rdquo;
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function AgentLibraryCitationButton({ displayIndex, source }: { displayIndex: number; source: AgentCitationSource }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => {
              const precise = toPrecisePreviewHighlight(
                source.highlightData ?? undefined,
                source.quote ?? null,
                `agent-lib-${source.documentId}-${displayIndex}`,
              )
              const targetPage = resolveCitationTargetPage(
                precise?.highlight ? { highlight: precise.highlight } : undefined,
                source.pageNumber,
              )
              void usePreviewStore.getState().openPreview({
                documentId: source.documentId,
                documentName: source.documentName,
                ...(targetPage != null && { page: targetPage }),
                context: 'chat',
                citationLabel: `Library [L${displayIndex}]`,
                ...(precise?.highlight ? { highlight: precise.highlight } : {}),
                ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
              })
            }}
            className="inline-flex items-center justify-center gap-0.5 text-[10px] font-semibold text-muted-foreground bg-muted hover:bg-muted/70 rounded-full ml-0.5 px-1 py-0.5 align-super leading-none transition-colors cursor-pointer"
            aria-label={`Library source ${displayIndex}: ${source.documentName}`}
          >
            <BookOpen className="h-2.5 w-2.5" />
            L{displayIndex}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] bg-popover text-popover-foreground border shadow-md">
          <p className="text-xs font-medium">{source.documentName}</p>
          {source.pageNumber ? (
            <p className="text-meta">Page {source.pageNumber}</p>
          ) : null}
          {(source.quote ?? source.snippet) && (
            <p className="text-meta mt-1 line-clamp-3">
              &ldquo;{source.quote ?? source.snippet}&rdquo;
            </p>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">Library document</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ===================
// Source Chip (below message)
// ===================

/**
 * One chip per cited DOCUMENT, carrying one page pill per cited chunk.
 * A document cited on four pages used to render as four near-identical
 * truncated chips; it now reads as one source with four page pills, and each
 * pill still deep-links to its own page.
 */
function SourceChip({
  group,
  sources,
  icon,
  prefix = '',
}: {
  group: GroupedCitationDoc<AgentCitationSource>
  sources: AgentCitationSource[]
  icon: React.ReactNode
  prefix?: string
}) {
  const firstChunk = group.chunks[0]
  if (!firstChunk) return null

  const firstSource = group.firstSource
  const firstPrecise = toPrecisePreviewHighlight(
    firstSource.highlightData ?? undefined,
    firstSource.quote ?? null,
    `agent-chip-${prefix}${group.documentId}-${firstChunk.displayIndex}`,
  )
  const firstTargetPage = resolveCitationTargetPage(
    firstPrecise?.highlight ? { highlight: firstPrecise.highlight } : undefined,
    firstSource.pageNumber,
  )

  return (
    /* Container, NOT a button — the name and each page pill are sibling
       buttons inside it, and a button cannot nest. */
    <span className="inline-flex items-center gap-1.5 py-0.5 pl-2 pr-1 rounded-full border border-border bg-card text-meta">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
              onClick={() => {
                void usePreviewStore.getState().openPreview({
                  documentId: firstSource.documentId,
                  documentName: firstSource.documentName,
                  ...(firstTargetPage != null && { page: firstTargetPage }),
                  context: 'chat',
                  citationLabel: prefix
                    ? `Library [${prefix}${firstChunk.displayIndex}]`
                    : `Source [${firstChunk.displayIndex}]`,
                  ...(firstPrecise?.highlight ? { highlight: firstPrecise.highlight } : {}),
                  ...(firstPrecise?.textHighlight ? { textHighlight: firstPrecise.textHighlight } : {}),
                })
              }}
            >
              {icon}
              <span className="truncate max-w-[200px]">{group.documentName}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent className="bg-popover text-popover-foreground border shadow-md">
            <p className="text-xs">{group.documentName}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {group.chunks.map((chunk) => {
        const source = sources[chunk.origIndex - 1]
        if (!source) return null

        const precise = toPrecisePreviewHighlight(
          source.highlightData ?? undefined,
          source.quote ?? null,
          `agent-chip-${prefix}${source.documentId}-${chunk.displayIndex}`,
        )
        const targetPage = resolveCitationTargetPage(
          precise?.highlight ? { highlight: precise.highlight } : undefined,
          source.pageNumber,
        )

        return (
          <TooltipProvider key={`${prefix}${chunk.origIndex}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  // Each pill is its own citation: without a name of its own,
                  // pills "2" and "3" were read as one number, "23".
                  aria-label={`Citation ${prefix}${chunk.displayIndex}${source.pageNumber ? `, page ${source.pageNumber}` : ''}`}
                  // The tinted hairline draws each pill's edge, so neighbouring
                  // pills read as separate shapes (Status Pills anatomy).
                  className="inline-flex items-center gap-0.5 h-4 shrink-0 rounded-full border border-[hsl(var(--brand)/0.2)] bg-[hsl(var(--brand-soft))] px-1.5 text-[9.5px] font-semibold text-[hsl(var(--ai-accent-text))] hover:opacity-80 transition-opacity"
                  onClick={() => {
                    void usePreviewStore.getState().openPreview({
                      documentId: source.documentId,
                      documentName: source.documentName,
                      ...(targetPage != null && { page: targetPage }),
                      context: 'chat',
                      citationLabel: prefix
                        ? `Library [${prefix}${chunk.displayIndex}]`
                        : `Source [${chunk.displayIndex}]`,
                      ...(precise?.highlight ? { highlight: precise.highlight } : {}),
                      ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
                    })
                  }}
                >
                  {prefix}{chunk.displayIndex}
                </button>
              </TooltipTrigger>
              <TooltipContent className="bg-popover text-popover-foreground border shadow-md">
                <p className="text-xs">{source.documentName}</p>
                {source.pageNumber ? (
                  <p className="text-meta">Page {source.pageNumber}</p>
                ) : null}
                {(source.quote ?? source.snippet) && (
                  <p className="text-meta mt-1 max-w-[250px] line-clamp-3">
                    {source.quote ?? source.snippet}
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      })}
    </span>
  )
}

// ===================
// Empty State
// ===================

function EmptyState({
  scopeKind,
  onPlanResponse,
  planDisabled,
  adviceOnly = false,
  hint,
}: {
  scopeKind: 'unit' | 'artifact'
  onPlanResponse: () => void
  planDisabled: boolean
  /** Advice-only (R1 E1f): no drafting chips, plan button or drafting hint. */
  adviceOnly?: boolean
  /** The surface's own one-line empty state (e.g. a buyer form). */
  hint?: string | undefined
}) {
  const target = scopeKind === 'artifact' ? 'document' : 'section'
  if (hint) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <div className="h-12 w-12 rounded-full flex items-center justify-center mb-3">
          <BrandIcon size={24} alt="" />
        </div>
        <p className="text-meta max-w-[240px]">{hint}</p>
      </div>
    )
  }
  if (adviceOnly) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <div className="h-12 w-12 rounded-full flex items-center justify-center mb-3">
          <BrandIcon size={24} alt="" />
        </div>
        <h4 className="text-sm font-medium mb-1">Draft Agent</h4>
        <p className="text-meta max-w-[240px]">Your AI assistant for this {target}.</p>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div className="h-12 w-12 rounded-full flex items-center justify-center mb-3">
        <BrandIcon size={24} alt="" />
      </div>
      <h4 className="text-sm font-medium mb-1">Draft Agent</h4>
      <p className="text-meta mb-4 max-w-[240px]">
        Your AI assistant for this {target}. It can:
      </p>
      <div className="flex flex-wrap justify-center gap-1.5 mb-4 max-w-[260px]">
        <Badge variant="outline" className="gap-1 text-[11px] font-normal cursor-default">
          <Search className="h-3 w-3" />
          Search evidence
        </Badge>
        <Badge variant="outline" className="gap-1 text-[11px] font-normal cursor-default">
          <ListChecks className="h-3 w-3" />
          Plan response
        </Badge>
        <Badge variant="outline" className="gap-1 text-[11px] font-normal cursor-default">
          <PenLine className="h-3 w-3" />
          Draft content
        </Badge>
        <Badge variant="outline" className="gap-1 text-[11px] font-normal cursor-default">
          <RefreshCw className="h-3 w-3" />
          Revise drafts
        </Badge>
        <Badge variant="outline" className="gap-1 text-[11px] font-normal cursor-default">
          <ShieldCheck className="h-3 w-3" />
          Verify compliance
        </Badge>
      </div>
      <Button
        variant="ai-outline"
        className="mb-4 gap-2"
        onClick={onPlanResponse}
        disabled={planDisabled}
      >
        <ListChecks className="h-4 w-4" />
        Create a response plan
      </Button>
      <p className="text-[11px] text-muted-foreground max-w-[240px]">
        Or ask for anything else — try &ldquo;Draft based on the tender requirements&rdquo;
      </p>
    </div>
  )
}


// ===================
// Evidence Badge with Tooltip
// ===================

function EvidenceBadge({
  selectedEvidence,
  evidenceNames,
  onClearEvidence,
}: {
  selectedEvidence: string[]
  evidenceNames: Record<string, string>
  onClearEvidence: () => void
}) {
  const documents = useDocumentsStore((s) => s.documents)
  // The picker supplies names for library AND tender picks; the bid-scoped
  // store only knows the latter, so it is the fallback, not the source.
  const docNames = selectedEvidence
    .map((id) => evidenceNames[id] ?? documents.find((d) => d.id === id)?.name)
    .filter((n): n is string => !!n)

  // One pinned doc reads better as its name than as a count; the chip truncates
  // and the tooltip carries the full name.
  const singleName = selectedEvidence.length === 1 ? docNames[0] : undefined
  const label = singleName
    ?? `${selectedEvidence.length} evidence doc${selectedEvidence.length === 1 ? '' : 's'}`

  return (
    <div className="flex items-center gap-1 mb-2 min-w-0">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="text-xs h-5 cursor-default max-w-[220px] min-w-0">
              <span className="truncate">{label}</span>
            </Badge>
          </TooltipTrigger>
          {docNames.length > 0 && (
            <TooltipContent side="top" className="max-w-[280px]">
              <ul className="text-xs space-y-0.5">
                {docNames.map((name) => (
                  <li key={name} className="truncate">{name}</li>
                ))}
              </ul>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <button
        onClick={onClearEvidence}
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}

// ===================
// Input Area
// ===================

interface InputAreaProps {
  value: string
  onChange: (v: string) => void
  onSend: () => void
  onStop: () => void
  isLoading: boolean
  selectedEvidence: string[]
  evidenceNames: Record<string, string>
  evidenceScope: EvidenceScope
  onOpenEvidence: () => void
  onClearEvidence: () => void
  placeholder: string
  /** The placeholder while text is selected in the editor. */
  selectionPlaceholder: string
  disabled: boolean
  selectionChip: { text: string } | null
  onDismissSelection: () => void
  suggestedOffer: SuggestedActionOffer | null
  showSuggestedOffer: boolean
  onSuggestedActionClick: (action: SuggestedAction) => void
  attachments: ReturnType<typeof useAssistantAttachments>
}

function InputArea({
  value,
  onChange,
  onSend,
  onStop,
  isLoading,
  selectedEvidence,
  evidenceNames,
  evidenceScope,
  onOpenEvidence,
  onClearEvidence,
  placeholder,
  selectionPlaceholder,
  disabled,
  selectionChip,
  onDismissSelection,
  suggestedOffer,
  showSuggestedOffer,
  onSuggestedActionClick,
  attachments: attachmentState,
}: InputAreaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-focus textarea when a selection chip appears, but only after a short
  // delay so we don't steal focus from the editor mid-drag selection
  useEffect(() => {
    if (!selectionChip || !textareaRef.current) return
    const timer = setTimeout(() => {
      // Only focus if the editor doesn't currently have focus (user finished selecting)
      const editorEl = document.querySelector('.tiptap.ProseMirror')
      if (!editorEl?.contains(document.activeElement)) {
        textareaRef.current?.focus()
      }
    }, 50)
    return () => clearTimeout(timer)
  }, [selectionChip])

  // Auto-grow textarea with content, capped at 4 rows (100px = 4×20px line + 20px padding)
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`
  }, [value])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!isLoading && !attachmentState.isUploading && !attachmentState.hasErrors && value.trim()) onSend()
    }
  }

  return (
    <div className="px-4 py-3">
      {showSuggestedOffer && suggestedOffer && (
        <div className="mb-2 space-y-1.5">
          <div className="flex flex-wrap gap-1.5">
            {suggestedOffer.actions.map((action) => (
              <Button
                key={action.actionId}
                type="button"
                size="sm"
                variant="secondary"
                // Offers are the primary next step after a reply, but ghost left
                // them reading as caption text. Tonal fill makes them legible as
                // controls at rest without adding a box to a chip row.
                className="h-7 text-xs"
                disabled={isLoading || disabled}
                onClick={() => onSuggestedActionClick(action)}
              >
                {action.label}
              </Button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Or type your own instruction</p>
        </div>
      )}

      {/* Selection chip */}
      {selectionChip && (
        <div className="flex items-center gap-1.5 mb-2">
          <TextSelect className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--brand))]" />
          <Badge variant="outline" className="text-xs h-auto py-0.5 max-w-[calc(100%-2rem)] font-normal border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand-soft)/0.5)] text-[hsl(var(--brand))]">
            <span className="truncate">
              &ldquo;{selectionChip.text.length > 60
                ? selectionChip.text.slice(0, 60) + '...'
                : selectionChip.text}&rdquo;
            </span>
          </Badge>
          <button
            onClick={onDismissSelection}
            className="text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] shrink-0"
            aria-label="Clear selection"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Evidence chips — only the "documents" scope pins anything */}
      {evidenceScope === 'documents' && selectedEvidence.length > 0 && (
        <EvidenceBadge
          selectedEvidence={selectedEvidence}
          evidenceNames={evidenceNames}
          onClearEvidence={onClearEvidence}
        />
      )}

      {/* Unified input box — matches AI Assistant / Tender Chat pattern:
          textarea and action row are separate flex siblings inside a shared
          bordered container, so text content can never overlap the buttons. */}
      <div className="border rounded-2xl bg-card transition-colors focus-within:ring-[3px] focus-within:ring-[hsl(var(--brand)/0.16)] focus-within:border-[hsl(var(--brand))] border-[hsl(var(--border))]">
        {/* Attachment chips */}
        {attachmentState.hasAttachments && (
          <ChatAttachments
            attachments={attachmentState.attachments}
            onRemove={attachmentState.removeAttachment}
          />
        )}

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handleUnwrappingPaste}
          placeholder={selectionChip ? selectionPlaceholder : placeholder}
          className="w-full min-h-[60px] max-h-[100px] resize-none bg-transparent px-3 pt-3 pb-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isLoading || disabled}
          rows={2}
        />

        <div className="flex items-center gap-1.5 px-2 pb-2">
          <AttachmentButton
            onFiles={attachmentState.addFiles}
            onSharePointItems={attachmentState.addSharePointItems}
            remainingSlots={MAX_CHAT_ATTACHMENTS - attachmentState.attachments.length}
            remainingBytes={MAX_CHAT_TOTAL_SIZE - attachmentState.totalSize}
            draftStashKey={DRAFT_STASH_KEYS.draftPanel}
            draftText={value}
            disabled={!attachmentState.canAttach || isLoading || disabled}
            active={attachmentState.hasAttachments}
          />
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 text-xs px-1.5",
              evidenceScope !== 'auto'
                ? "text-[hsl(var(--brand))] font-medium hover:text-[hsl(var(--brand))]"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={onOpenEvidence}
            disabled={isLoading || disabled}
            aria-pressed={evidenceScope !== 'auto'}
          >
            <Library className="h-3.5 w-3.5 mr-1" />
            {evidenceScope === 'fact_bank'
              ? 'Evidence · Fact Bank'
              : evidenceScope === 'documents' && selectedEvidence.length > 0
                ? `Evidence · ${selectedEvidence.length} doc${selectedEvidence.length === 1 ? '' : 's'}`
                : 'Evidence'}
          </Button>
          <div className="flex-1" />
          <Button
            size="icon"
            className={cn(
              'h-7 w-7 rounded-full bg-ai text-ai-foreground hover:bg-ai/90',
              isLoading && 'bg-muted text-muted-foreground hover:bg-muted'
            )}
            onClick={isLoading ? onStop : onSend}
            disabled={!isLoading && (!value.trim() || disabled || attachmentState.isUploading || attachmentState.hasErrors)}
            variant={isLoading ? 'ghost' : 'default'}
            aria-label={isLoading ? 'Stop' : 'Send'}
          >
            {isLoading ? (
              <Square className="h-3.5 w-3.5" />
            ) : (
              <ArrowUp className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1 text-right">
        {isLoading ? 'Click stop to cancel' : `Enter to send · Shift+Enter for new line`}
      </p>
    </div>
  )
}

// ===================
// Main Component
// ===================

function isCrossSectionRewriteRequest(message: string): boolean {
  return /\b(all sections?|every section|entire (?:document|proposal)|whole (?:response|proposal)|across sections?|multi-?section)\b/i.test(message)
}


function WriteTabBody(props: UnifiedAiPanelProps) {
  const { bidId, artifactId, sectionKey, scopeKind = 'unit', adviceOnly, emptyHint } = props
  const isAdviceOnly = adviceOnly !== undefined
  const {
    sessionId,
    initSession,
    activeScope,
    threads,
    activeThreadId,
    threadPreviewById,
    messages,
    currentTurnId,
    isStreaming,
    isCancellable,
    isPolling,
    isEditingSelection,
    isResolvingDraftPatchPreview,
    selection,
    draftPatchPreview,
    writingWorkflowState,
    streamRecoveryState,
    errorAction,
    isLockedByAnotherTab,
    documentJobEnabled,
    activeSuggestedOffer,
    activityPhases,
    error,
    clearError,
    clearSuggestedOffer,
    sendTurn,
    cancelTurn,
    loadThreads,
    createThread,
    switchThread,
    editCurrentSelection,
    appendAssistantMessage,
    recoverFromRevisionConflict,
  } = useAgentStore()

  // Resume polling: when initSession detects an in-flight turn from another
  // section/tab/device, streamRecoveryState flips to 'recovering' and
  // currentTurnId is seeded. The poller watches that turn's status via the
  // backend and reconciles the session when it hits a terminal state. No-op
  // when nothing is recovering.
  useActiveTurnPoller(
    streamRecoveryState === 'recovering' ? sessionId : null,
    streamRecoveryState === 'recovering' ? currentTurnId : null
  )

  const outline = useDraftingStore((s) => s.outline)
  const sectionTitle = outline?.sections.find((s) => s.sectionKey === sectionKey)?.title

  const selectionChip = selection ? { text: selection.text } : null
  const clearSelectionEdit = useAgentStore((s) => s.clearSelectionEdit)

  const [inputValue, setInputValue] = useState('')
  const [selectedEvidence, setSelectedEvidence] = useState<string[]>([])
  // Names for the pinned ids, sourced from the picker so library + tender docs
  // can both be labelled without a bid-scoped store lookup.
  const [evidenceNames, setEvidenceNames] = useState<Record<string, string>>({})
  const [evidenceScope, setEvidenceScope] = useState<EvidenceScope>('auto')
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false)
  const chatAttachments = useAssistantAttachments()
  const [suggestedOfferCollapsed, setSuggestedOfferCollapsed] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isClassifyingIntent, setIsClassifyingIntent] = useState(false)
  const sendInflightRef = useRef(false)

  // Dropping the pin must drop the scope with it — a highlighted Evidence
  // button over an empty selection is a lie (E11).
  const clearEvidencePin = useCallback(() => {
    setSelectedEvidence([])
    setEvidenceNames({})
    setEvidenceScope('auto')
  }, [])

  // Initialize agent session when panel mounts or section changes
  useEffect(() => {
    if (bidId && artifactId && (sectionKey || scopeKind === 'artifact')) {
      void initSession(bidId, artifactId, sectionKey, scopeKind)
      setInputValue('')
      clearEvidencePin()
      setSuggestedOfferCollapsed(false)
    }
  }, [bidId, artifactId, sectionKey, scopeKind, initSession, clearEvidencePin])

  // Restore a draft stashed before the SharePoint connect redirect. Consume is
  // one-shot, and an already-typed message always wins over the stash.
  // Declared AFTER the init effect on purpose: that one clears the composer on
  // mount, and effects flush in declaration order, so restoring first would be
  // wiped.
  useEffect(() => {
    const stashed = consumeDraftStash(DRAFT_STASH_KEYS.draftPanel)
    if (stashed) setInputValue((prev) => prev || stashed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Consume the one-shot input seed set by Fix-with-AI.
  //
  // Declared AFTER the init effect for the same reason the stash restore above
  // is: that effect clears the composer on mount, effects flush in declaration
  // order, and seeding before it would be wiped. Clicking Fix-with-AI from the
  // Review tab is exactly that case — it switches to this tab, so the seed
  // landed and was erased in the same commit. The user saw an empty composer,
  // no turn was ever sent, and the finding sat on "Fixing…" forever.
  //
  // Peek before consuming. The previous version consumed unconditionally and
  // pushed the seed back when the thread switch hadn't landed yet, which cannot
  // re-trigger the effect: it depended on a `pendingInputSeed !== null` boolean
  // that a put-back leaves unchanged. Reading the values directly means the
  // effect re-runs when either the seed or the active thread changes, and
  // nothing leaves the store until it is actually applied.
  const pendingSeed = useUnifiedAiPanelStore((s) => s.pendingInputSeed)
  const pendingSeedThreadId = useUnifiedAiPanelStore((s) => s.pendingThreadId)
  const consumePendingSeed = useUnifiedAiPanelStore((s) => s.consumePendingSeed)
  useEffect(() => {
    if (pendingSeed === null) return
    // Hold while agent-store is still switching: applying now would drop the
    // instruction into the thread the user is being moved away from.
    if (pendingSeedThreadId && activeThreadId && pendingSeedThreadId !== activeThreadId) return
    consumePendingSeed()
    setInputValue(pendingSeed)
  }, [pendingSeed, pendingSeedThreadId, activeThreadId, consumePendingSeed])

  useEffect(() => {
    setSuggestedOfferCollapsed(false)
  }, [activeSuggestedOffer?.offerId])

  useEffect(() => {
    if (!activeSuggestedOffer) return
    const remainingMs = Date.parse(activeSuggestedOffer.expiresAt) - Date.now()
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
      clearSuggestedOffer()
      return
    }

    const timer = setTimeout(() => {
      clearSuggestedOffer()
    }, remainingMs + 50)

    return () => clearTimeout(timer)
  }, [activeSuggestedOffer, clearSuggestedOffer])

  // Auto-scroll to bottom on new messages or workflow state changes
  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
      })
    }
  }, [messages, isStreaming, isPolling, writingWorkflowState])

  const handleSend = async () => {
    const msg = inputValue.trim()
    if (!msg || isStreaming || isPolling || !sessionId || chatAttachments.isUploading || chatAttachments.hasErrors) return
    if (sendInflightRef.current) return

    // A request about several sections is answered here only while the panel has no route for it (R1 E1f):
    // advice-only edits nothing, so it is a question for the AI; with the whole-document job offered
    // (`documentJobEnabled`, from the session), start_job takes it, as the backend's phrase rule allows.
    // TODO(R1 F5′): delete this block with the backend's phrase rule and the old draft-all engine.
    if (!isAdviceOnly && !documentJobEnabled && !selection && effectiveScopeKind === 'unit' && isCrossSectionRewriteRequest(msg)) {
      appendAssistantMessage(
        'This panel can only edit the currently open section. Tell me the exact paragraph or section-level change to apply here.'
      )
      // Deliberately NOT clearing the composer. This asks the user to restate
      // their request more narrowly — and used to delete the words they would
      // narrow, forcing them to retype from memory. Nothing was sent, so there
      // is nothing to reset; keeping the text is what makes the instruction
      // actionable.
      return
    }

    // Answer-time scope (Layer 1): "documents" pins the selected docs; "fact_bank"
    // hard-scopes to the Fact Bank; "auto" lets the AI pick.
    const evidence = evidenceScope === 'documents' && selectedEvidence.length > 0 ? selectedEvidence : undefined
    const factBankOnly = evidenceScope === 'fact_bank'
    const attachmentRefs = chatAttachments.getReadyRefs()
    // The composer clears optimistically so the panel feels responsive. Hold on
    // to what was cleared: `sendTurn` resolves (rather than throwing) on a
    // turn_error, so a rejected turn would otherwise destroy the user's typed
    // instruction and force them to re-pick their files from disk, with no hint
    // that is what happened.
    const clearedInput = inputValue
    const clearedAttachments = chatAttachments.attachments
    setInputValue('')
    chatAttachments.clearAttachments()

    sendInflightRef.current = true
    try {
      if (selection && isAdviceOnly) {
        // Advice-only (R1 E1f): a question about the selected text, never an edit of it.
        await sendTurn(msg, evidence, {
          quotedText: selection.text,
          adviceOnly: true,
          ...(factBankOnly ? { factBankOnly: true } : {}),
          ...(attachmentRefs.length > 0 ? { attachments: attachmentRefs } : {}),
        })
        return
      }

      if (selection) {
        // Decide whether the user is asking about the selection or wants
        // to edit it. The legacy behavior was to always edit, which
        // misfired on questions like "is this sentence ok?".
        let intent: 'edit' | 'ask' = 'edit'
        setIsClassifyingIntent(true)
        try {
          const result = await classifySelectionIntent({
            selectedText: selection.text,
            instruction: msg,
          })
          intent = result.intent
        } catch {
          // Classifier failed — preserve legacy behavior (edit).
          intent = 'edit'
        } finally {
          setIsClassifyingIntent(false)
        }

        if (intent === 'ask') {
          await sendTurn(msg, evidence, {
            quotedText: selection.text,
            ...(factBankOnly ? { factBankOnly: true } : {}),
            ...(attachmentRefs.length > 0 ? { attachments: attachmentRefs } : {}),
          })
          return
        }

        try {
          await editCurrentSelection(sessionId, msg, evidence, {
            ...(attachmentRefs.length > 0 ? { attachments: attachmentRefs } : {}),
            ...(factBankOnly ? { factBankOnly: true } : {}),
          })
        } catch {
          // store handles error state
        }
        return
      }

      await sendTurn(msg, evidence, {
        ...(isAdviceOnly ? { adviceOnly: true } : {}),
        ...(factBankOnly ? { factBankOnly: true } : {}),
        ...(attachmentRefs.length > 0 ? { attachments: attachmentRefs } : {}),
      })
    } finally {
      sendInflightRef.current = false
      // `sendTurn` clears `error` before it streams, so an error present once it
      // resolves belongs to this send — either a pre-flight refusal (session
      // busy, pending preview) or a turn_error off the stream. Read it live:
      // the subscribed value in this closure is a render behind.
      if (useAgentStore.getState().error) {
        setInputValue((current) => (current.trim().length > 0 ? current : clearedInput))
        chatAttachments.restoreAttachments(clearedAttachments)
      }
    }
  }

  const handleInputChange = (nextValue: string) => {
    setInputValue(nextValue)
    if (!activeSuggestedOffer) return
    if (nextValue.trim().length === 0) {
      setSuggestedOfferCollapsed(false)
      return
    }
    setSuggestedOfferCollapsed(true)
  }

  // isTurnLoading alone cannot debounce a double-click: sendTurn awaits a draft
  // flush and an auth-token fetch before it sets isStreaming, so a second click
  // inside that window slips through and fires a duplicate turn. sendInflightRef
  // is the synchronous latch (same guard handleSend uses).
  const handleSuggestedActionClick = async (action: SuggestedAction) => {
    if (!activeSuggestedOffer || isTurnLoading || !sessionId) return
    if (sendInflightRef.current) return
    setInputValue('')
    clearEvidencePin()
    setSuggestedOfferCollapsed(false)
    sendInflightRef.current = true
    try {
      await sendTurn(action.utterance, undefined, {
        ...(isAdviceOnly ? { adviceOnly: true } : {}),
        suggestedOfferId: activeSuggestedOffer.offerId,
        suggestedActionId: action.actionId,
        suggestedActionSourceTurnId: activeSuggestedOffer.sourceTurnId,
      })
    } finally {
      sendInflightRef.current = false
    }
  }

  // Empty-state CTA (3C): the only discoverable entry point into plan
  // generation from this panel. Sends a plain write-intent utterance — the
  // same path a typed ask takes — so no new store surface is needed.
  const handlePlanResponse = async () => {
    if (isTurnLoading || !sessionId) return
    if (sendInflightRef.current) return
    const target = effectiveScopeKind === 'artifact' ? 'document' : 'section'
    sendInflightRef.current = true
    try {
      await sendTurn(`Create a response plan for this ${target}.`)
    } finally {
      sendInflightRef.current = false
    }
  }

  const handleStop = () => {
    void cancelTurn()
  }

  const isTurnLoading = isStreaming || isPolling || isCancellable || isClassifyingIntent
  const isThreadUiDisabled = isEditingSelection || isResolvingDraftPatchPreview
  const effectiveScopeKind = activeScope?.kind ?? scopeKind

  const focusScopeLabel = effectiveScopeKind === 'artifact'
    ? 'Entire document'
    : (sectionTitle ?? 'Current section')
  const inputPlaceholder = !sessionId
    ? (error ? 'Session unavailable. Retry connection to continue.' : 'Connecting...')
    : isAdviceOnly
      ? `Ask for advice on this ${effectiveScopeKind === 'artifact' ? 'document' : 'section'}`
      : 'Ask, or describe what to write'
  const recoveryBadgeLabel = streamRecoveryState === 'recovering'
    ? 'Recovering stream'
    : streamRecoveryState === 'replay_complete'
      ? 'Replay complete'
      : streamRecoveryState === 'replay_unavailable'
        ? 'Replay unavailable'
        : null

  return (
    <div className="flex flex-col h-full">
      {/* Header — scope row only. The enclosing Tabs trigger row (Ask AI /
          Review / Comment) already identifies this pane as the Ask AI agent,
          so the old 'Draft Agent' subheader is redundant. Session-in-use
          and stream-recovery badges moved into the scope row. */}
      <div className="shrink-0">
        {(effectiveScopeKind === 'artifact' || effectiveScopeKind === 'unit') && (
          <div className="flex items-center gap-2 px-4 py-1.5">
            <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="truncate text-[11px] font-medium text-foreground">
              {focusScopeLabel}
            </span>
            <span className="ml-auto shrink-0 flex items-center gap-1">
              {isLockedByAnotherTab && (
                <Badge variant="outline" className="h-5 text-[10px]">
                  Session in use
                </Badge>
              )}
              {recoveryBadgeLabel && (
                <Badge variant="outline" className="h-5 text-[10px]">
                  {recoveryBadgeLabel}
                </Badge>
              )}
              <ThreadPicker
                scopeKind={effectiveScopeKind}
                threads={threads}
                activeThreadId={activeThreadId}
                threadPreviewById={threadPreviewById}
                disabled={isThreadUiDisabled || !sessionId}
                onLoadThreads={loadThreads}
                onSwitchThread={switchThread}
              />
              <ThreadCreateButton
                disabled={isThreadUiDisabled || !sessionId}
                onCreateThread={createThread}
              />
            </span>
          </div>
        )}
      </div>

      {/* Advice-only (R1 E1f): the document is finalised or in review, so the AI advises and changes nothing. */}
      {adviceOnly && (
        <div
          className="mx-4 mb-1 flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1.5"
          data-testid="advice-only-note"
        >
          <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="text-meta">{ADVICE_ONLY_NOTES[adviceOnly]}</span>
        </div>
      )}

      {/* A document job on this document: its saved progress, links and Cancel (R1 P3′). Not on an advice-only panel. */}
      {!isAdviceOnly && <PanelDocumentJob artifactId={artifactId} className="mx-4 mb-1" />}

      {/* Staged suggestions waiting on this document (R1). Not on an advice-only panel. */}
      {!isAdviceOnly && <PanelPendingSuggestions bidId={bidId} artifactId={artifactId} className="mx-4 mb-1" />}

      {/* Error banner */}
      {error && (
        <div className="mx-4 mb-1 flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-md px-2 py-1.5">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{error}</span>
          {errorAction === 'refresh_retry_conflict' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => { void recoverFromRevisionConflict() }}
              disabled={isTurnLoading}
            >
              Get latest draft and retry
            </Button>
          )}
          {!sessionId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => { void initSession(bidId, artifactId, sectionKey, scopeKind) }}
            >
              Retry
            </Button>
          )}
          <button onClick={clearError} className="shrink-0">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0 scrollbar-thin">
        {messages.length === 0 && !isStreaming && !isPolling ? (
          <EmptyState
            scopeKind={effectiveScopeKind}
            onPlanResponse={() => void handlePlanResponse()}
            planDisabled={!sessionId || isTurnLoading}
            adviceOnly={isAdviceOnly}
            hint={emptyHint}
          />
        ) : (
          <>
            {messages
              // Activity rows replace the TRANSIENT status narration, never the
              // durable lines (cancelled / background finished / failed). Those
              // are the only confirmation the writer gets, so they always render.
              .filter((m) => !(m.role === 'status' && m.durable !== true && activityPhases.phases.length > 0))
              .filter((m) => !(m.role === 'assistant' && m.isStreaming && m.content.trim().length === 0 && !m.memorySaved?.length))
              .map((m) => (
                <div key={m.id} className="space-y-2">
                  {/* Show hydrated activity phases above historical assistant messages */}
                  {m.role === 'assistant' && m.persistedSteps && m.persistedSteps.length > 0 && (
                    <ActivityPhases phases={hydratePhasesFromSteps(m.persistedSteps)} isStreaming={false} />
                  )}
                  <MessageBubble message={m} adviceOnly={isAdviceOnly} />
                </div>
              ))}
            {/* Live activity phases for current turn */}
            {activityPhases.phases.length > 0 && (
              // `isStreaming` drops to false the moment the turn hands off to
              // the background worker, so the block must also stay open while
              // the job is polled — otherwise it reads "done" mid-draft. The
              // same holds for a turn resumed after a section switch: its rows
              // are rebuilt from replayed events and may all be settled while
              // the turn is still running, so recovering counts as live too.
              <ActivityPhases
                phases={activityPhases}
                isStreaming={isStreaming || isPolling || streamRecoveryState === 'recovering'}
              />
            )}
          </>
        )}
        {/* The AI's questions about this document (R1 Q2). */}
        <PanelQuestions bidId={bidId} artifactId={artifactId} />
      </div>

      {/* Stream recovery guidance */}
      {streamRecoveryState === 'replay_unavailable' && (
        <div className="mx-4 my-2 rounded-lg border border-border bg-muted/50 p-3 space-y-2">
          <p className="text-meta">
            The previous response could not be recovered.
          </p>
          {messages.length > 0 && (() => {
            const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')
            if (!lastUserMsg) return null
            return (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={isTurnLoading || !sessionId}
                onClick={() => void sendTurn(lastUserMsg.content, undefined, isAdviceOnly ? { adviceOnly: true } : undefined)}
              >
                Retry last message
              </Button>
            )
          })()}
        </div>
      )}

      {/* Input area */}
      <InputArea
        value={inputValue}
        onChange={handleInputChange}
        onSend={() => void handleSend()}
        onStop={handleStop}
        isLoading={isTurnLoading}
        selectedEvidence={selectedEvidence}
        evidenceNames={evidenceNames}
        evidenceScope={evidenceScope}
        onOpenEvidence={() => setEvidenceDialogOpen(true)}
        onClearEvidence={clearEvidencePin}
        placeholder={inputPlaceholder}
        selectionPlaceholder={isAdviceOnly ? 'What do you want to know about this?' : 'What do you want to change?'}
        // A suggestion waiting in the editor holds the composer, except where it cannot be decided (advice-only, R1 E1f).
        disabled={!sessionId || isPolling || (!!draftPatchPreview && !isAdviceOnly) || isEditingSelection || isResolvingDraftPatchPreview}
        selectionChip={selectionChip}
        onDismissSelection={() => clearSelectionEdit()}
        suggestedOffer={activeSuggestedOffer}
        showSuggestedOffer={Boolean(activeSuggestedOffer && !suggestedOfferCollapsed)}
        onSuggestedActionClick={(action) => void handleSuggestedActionClick(action)}
        attachments={chatAttachments}
      />

      {/* Evidence dialog */}
      <EvidenceSelectDialog
        open={evidenceDialogOpen}
        onOpenChange={setEvidenceDialogOpen}
        selectedIds={selectedEvidence}
        selectedNames={evidenceNames}
        onSelectionChange={(ids, names, scope) => {
          // The scope in force at Apply wins: only "documents" keeps a pin.
          if (scope !== 'documents') {
            setSelectedEvidence([])
            setEvidenceNames({})
            setEvidenceScope(scope)
            return
          }
          setSelectedEvidence(ids)
          setEvidenceNames(names)
          // An empty selection under "documents" pins nothing, so fall back to
          // letting the AI choose.
          setEvidenceScope(ids.length > 0 ? 'documents' : 'auto')
        }}
        mode="agent"
        currentBidId={bidId}
        scope={evidenceScope}
      />
    </div>
  )
}

// ===================
// Tab shell — three-tab surface wrapping Ask AI / Checks / Comments
// ===================

export function UnifiedAiPanel(props: UnifiedAiPanelProps) {
  const { commentsOnly: commentsOnlyProp = false, reviewerTabs = false, initialTab, adviceOnly: adviceOnlyProp, scopeLabel } = props
  // A read-only user — an org viewer, or a member who is not on the bid —
  // gets the reader's panel wherever it is mounted: Ask AI and advice both
  // need an AI session, which the backend refuses them.
  // Until the role and the bid are known they look like an editor, and Ask AI
  // would open a session that 403s — so the panel waits (a hard load only).
  const bidAccess = useBidReadOnly(props.bidId)
  const isReadOnly = bidAccess.isReadOnly
  const commentsOnly = commentsOnlyProp || isReadOnly
  const adviceOnly = isReadOnly ? undefined : adviceOnlyProp
  const tab = useUnifiedAiPanelStore((s) => s.tab)
  const setTab = useUnifiedAiPanelStore((s) => s.setTab)
  const commentsByArtifact = useCommentsStore((s) => s.byTargetId)
  const openCommentCount = useMemo(
    () => (commentsByArtifact[props.artifactId] ?? []).filter((c) => c.status === 'open').length,
    [commentsByArtifact, props.artifactId],
  )

  // Readers get Comments only — force the tab rather than reading the persisted
  // one, and leave that preference untouched for when they open a writable unit.
  // The designated section reviewer additionally gets the Review tab (its
  // promote-to-comment flow is a reviewer permission); that selection is local
  // state, never the persisted store, for the same reason.
  //
  // Advice-only (R1 E1f): Ask AI and Comments read the persisted tab as a
  // writer's panel does; Checks only for the section's reviewer, as on any
  // read-only surface, so a remembered Checks tab opens on Ask AI instead.
  const showReviewTab = adviceOnly !== undefined ? reviewerTabs : !commentsOnly || reviewerTabs
  const [readOnlyTab, setReadOnlyTab] = useState<'review' | 'comment'>(initialTab ?? 'comment')
  // The parent owns the tab when it drives one (a marker click in the reader's
  // margin); local state still wins for the user's own clicks in between.
  useEffect(() => {
    if (initialTab) setReadOnlyTab(initialTab)
  }, [initialTab])
  const effectiveTab = commentsOnly
    ? (reviewerTabs ? readOnlyTab : 'comment')
    : tab === 'review' && !showReviewTab ? 'write' : tab
  const tabCount = (commentsOnly ? 0 : 1) + (showReviewTab ? 1 : 0) + 1

  // ----- Unread (spec D4) -----
  const { bidId, artifactId } = props
  const isPanelOpen = useAgentStore((s) => s.isPanelOpen)
  const markViewed = useCommentsStore((s) => s.markViewed)
  const clearViewSnapshot = useCommentsStore((s) => s.clearViewSnapshot)
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const unreadCutLine = useCommentsStore((s) => unreadReference(s, artifactId))
  // The cut-line is only knowable once the list GET has answered. Opening a
  // view session before then would snapshot a watermark of `null` and light up
  // every comment ever written as "New".
  const watermarkLoaded = useCommentsStore((s) =>
    Object.prototype.hasOwnProperty.call(s.watermarkByTargetId, artifactId),
  )
  const hasUnread = useMemo(
    () =>
      hasUnreadThreads(commentsByArtifact[artifactId] ?? [], unreadCutLine, currentUserId),
    [commentsByArtifact, artifactId, unreadCutLine, currentUserId],
  )

  /**
   * A view session lasts exactly as long as the Comments tab is the live view.
   * Opening it snapshots the cut-line and POSTs `comments-viewed`; the polling
   * hook re-POSTs from there. Leaving the tab (or collapsing the panel, which
   * unmounts this) closes it, so the next visit re-snapshots against the
   * advanced watermark and the previous visit's pills clear.
   */
  const commentsTabActive = effectiveTab === 'comment'
  useEffect(() => {
    if (!commentsTabActive || !isPanelOpen || !watermarkLoaded) return

    const open = (): void => {
      if (document.visibilityState !== 'visible') return
      void markViewed(bidId, artifactId)
    }

    // A backgrounded browser tab is not a reader: wait for them to come back
    // rather than closing the counterparty's edit window in their absence.
    open()
    document.addEventListener('visibilitychange', open)
    return () => {
      document.removeEventListener('visibilitychange', open)
      clearViewSnapshot(artifactId)
    }
  }, [
    commentsTabActive,
    isPanelOpen,
    watermarkLoaded,
    bidId,
    artifactId,
    markViewed,
    clearViewSnapshot,
  ])

  if (!bidAccess.isLoaded) return null

  return (
    <Tabs
      value={effectiveTab}
      onValueChange={(value) => {
        if (!commentsOnly) {
          setTab(value as AiPanelTab)
        } else if (reviewerTabs && (value === 'review' || value === 'comment')) {
          setReadOnlyTab(value)
        }
      }}
      className="flex flex-col h-full"
    >
      {scopeLabel && (
        <p className="shrink-0 px-4 pt-3 text-meta" data-testid="ai-panel-scope">
          {scopeLabel}
        </p>
      )}
      <TabsList
        className={cn(
          'shrink-0 mx-4 bg-muted p-1 h-9 rounded-lg grid gap-0',
          scopeLabel ? 'mt-2' : 'mt-3',
          tabCount === 3 ? 'grid-cols-3' : tabCount === 2 ? 'grid-cols-2' : 'grid-cols-1'
        )}
      >
        {!commentsOnly && (
          <TabsTrigger
            value="write"
            className="h-7 rounded-md text-sm inline-flex items-center justify-center gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm data-[state=active]:font-medium"
          >
            Ask AI
          </TabsTrigger>
        )}
        {showReviewTab && (
          <TabsTrigger
            value="review"
            className="h-7 rounded-md text-sm inline-flex items-center justify-center gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm data-[state=active]:font-medium"
          >
            Checks
          </TabsTrigger>
        )}
        <TabsTrigger
          value="comment"
          className="h-7 rounded-md text-sm inline-flex items-center justify-center gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm data-[state=active]:font-medium"
        >
          Comments
          {openCommentCount > 0 && (
            <Badge className="text-[10px] px-1.5 py-0 bg-card text-foreground border border-border flex-shrink-0">
              {openCommentCount}
            </Badge>
          )}
          {hasUnread && (
            <>
              <span
                data-testid="comments-unread-dot"
                aria-hidden="true"
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--brand))]"
              />
              <span className="sr-only">unread</span>
            </>
          )}
        </TabsTrigger>
      </TabsList>
      {isReadOnly && <ViewOnlyNotice message={bidAccess.message} className="shrink-0 mx-4 mt-2" />}
      {!commentsOnly && (
        <TabsContent
          value="write"
          forceMount
          className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden focus-visible:ring-0"
        >
          <WriteTabBody {...props} />
        </TabsContent>
      )}
      {showReviewTab && (
        <TabsContent
          value="review"
          forceMount
          className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden focus-visible:ring-0"
        >
          {props.renderChecks ? props.renderChecks() : <ReviewTab {...props} />}
        </TabsContent>
      )}
      <TabsContent
        value="comment"
        forceMount
        className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden focus-visible:ring-0"
      >
        <CommentsTab
          bidId={props.bidId}
          artifactId={props.artifactId}
          scopeKind={props.scopeKind}
          bidMembers={props.bidMembers}
          jumpContext={props.commentJumpContext}
        />
      </TabsContent>
    </Tabs>
  )
}
